<template>
  <div>
    <div>
      <p>
        <span>
          Please enter the login code to validate your new contact information. Login code will be valid for limited time.
          If you entered email and cell phone # you will have received separate login code(s) and you will have to validate both.
          After validating both click 'Next' to navigate to your RI DMV Online Customer profile.
        </span> <br />
        <span>
          Your contact information will be saved against your RI record and used for future electronic Multi Factor Authentication (MFA).
        </span>
      </p>
      <p class="err-email" v-if="contactMfaRequestEmail.length > 0 && !contactMfaRequestEmail[0].isSucess">
        Unable to send login code to provided email
      </p>
      <div class="user-con-validate" v-if="updateContact.email">
        <div class="user-con-details">
          <div class="user-details">
            <span class="user-email-heading">Email</span> 
            <div class="verify-updateContact">
              <label for="email">{{ updateContactInfo.email }}</label>
              <img v-if="contactVerifyEmail" class="verify-icon" src="static/verified-icon.png" alt="Email is verified">
            </div>
          </div>
          <div class="user-details" v-if="!contactVerifyEmail">
            <div>
              <label for="loginCodeEmail" class="login-code-text">Login code</label>
              <input id="loginCodeEmail" type="text" v-model="otpEmail" maxlength="12">
              <p v-if="errEmail" class="err-msg">{{ errEmail }}</p>
            </div>
          </div>
        </div>
        <div class="user-response" v-if="!contactVerifyEmail">
          <div class="btn-otp">
            <CustomButton @click="validateOtp('Email')">Validate login code</CustomButton>
            <CustomButton @click="resendOtp('Email')" class="resend-code">Resend login code</CustomButton>
          </div>
        </div>
      </div>

      <p v-if="contactMfaRequestPhone.length > 0 && !contactMfaRequestPhone[0].isSucess" class="err-phone">
        Unable to send login code to provided phone
      </p>
      <div class="user-con-validate" v-if="updateContact.phone">
        <div class="user-con-details">
          <div class="user-details">
            <div class="user-phone-details">
              <span class="user-phone-heading">Phone</span>
              <span class="user-phone-text">(Message and data rates may apply)</span>
            </div>
            <div class="verify-updateContact">
              <label for="phone">{{ updateContactInfo.phone }}</label>
              <img v-if="contactVerifyPhone" class="verify-icon" src="static/verified-icon.png" alt="Phone is verified">
            </div>
          </div>
          <div class="user-details" v-if="!contactVerifyPhone">
            <div>
              <label for="loginCodePhone" class="login-code-text">Login code</label>
              <input id="loginCodePhone" type="text" v-model="otpPhone" maxlength="12">
              <p v-if="errPhone" class="err-msg">{{ errPhone }}</p>
            </div>
          </div>
        </div>
        <div class="user-response" v-if="!contactVerifyPhone">
          <div class="btn-otp">
            <CustomButton @click="validateOtp('Phone')">Validate login code</CustomButton>
            <CustomButton @click="resendOtp('Phone')" class="resend-code">Resend login code</CustomButton>
          </div>
        </div>
      </div>
    </div>

    <BaseButtonGroup>
      <button type="button" @click="handleRouting('previous')">Previous</button>
      <button type="submit" class="app-btn-primary" @click.prevent="handleRouting('next')" :disabled="!isNextButtonEnabled">
        Next
      </button>
    </BaseButtonGroup>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import * as COMMONS from '../../store/_commons'; 
import BaseButtonGroup from '@/components/ui/BaseButtonGroup.vue';
import CustomButton from '../CustomButton.vue';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';

// Vuex Store
const store = useStore();

// Vue Router
const router = useRouter();

// Reactive state
const otpPhone = ref('');
const otpEmail = ref('');
const errEmail = ref('');
const errPhone = ref('');
const emailCount = ref(0);
const phoneCount = ref(0);

// Phone composable
const { formatPhoneForRims } = usePhoneFormatter();

// Computed Properties
const updateContact = computed(() => store.state.guestLogin.updateContact);
const updateContactInfo = computed(() => store.state.guestLogin.contactUpdateInfo);
const contactVerifyEmail = computed(() => store.state.guestLogin.contactVerifyEmail);
const contactVerifyPhone = computed(() => store.state.guestLogin.contactVerifyPhone);

const contactMfaRequestPhone = computed(() => {
  const responses = store.state.guestLogin.contactMfaRequest.responses;
  return responses ? responses.filter(item => item.mfaType === 1) : [];
});

const contactMfaRequestEmail = computed(() => {
  const responses = store.state.guestLogin.contactMfaRequest.responses;
  return responses ? responses.filter(item => item.mfaType === 2) : [];
});

const isNextButtonEnabled = computed(() => {
  const hasEmail = updateContactInfo.value.email;
  const hasPhone = updateContactInfo.value.phone;
  const isEmailVerified = contactVerifyEmail.value;
  const isPhoneVerified = contactVerifyPhone.value;
  
  let isEnabled = false;

  if (hasEmail && !hasPhone) {
    isEnabled = isEmailVerified;
  }

  if (!hasEmail && hasPhone) {
    isEnabled = isPhoneVerified;
  }

  if (hasEmail && hasPhone) {
    isEnabled = isEmailVerified && isPhoneVerified;
  }

  return isEnabled; 
});


// Lifecycle Hooks
onMounted(() => {
  window.scroll(0, 0);
  document.body.setAttribute('tabindex', '1');
  document.body.focus();
  document.body.removeAttribute('tabindex');
  document.getElementById('app').scrollTop = 0;
});

// Methods 
const validateOtp = async (val) => {
  errPhone.value = '';
  errEmail.value = '';
  if (val == 'Phone') {
    if (otpPhone.value) {
      phoneCount.value++;
      const data = {
        mfaType: 1,
        verificationCode: otpPhone.value,
        attemptValue: phoneCount.value,
      };
      await store.dispatch(COMMONS.GUEST_VERIFY_MFA_REQUEST, data);
    } else {
      errPhone.value = 'Login code is required';
    }
  } 
  else if (otpEmail.value) {
    emailCount.value++;
    const data = {
      mfaType: 2,
      verificationCode: otpEmail.value,
      attemptValue: emailCount.value,
    };
    await store.dispatch(COMMONS.GUEST_VERIFY_MFA_REQUEST, data);
  } else {
    errEmail.value = 'Login code is required';
  }
};

const resendOtp = async (val) => {
  errPhone.value = '';
  errEmail.value = '';
  emailCount.value = 0;
  phoneCount.value = 0;
  let data = {};
  if (val == 'Email') {
    otpEmail.value = '';
    data.resendOtp = true;
    data.email = updateContactInfo.value?.email;
    data.phone = '';
  }
  if (val == 'Phone') {
    otpPhone.value = '';
    data.resendOtp = true;
    data.email = '';
    data.phone = formatPhoneForRims(updateContactInfo.value?.phone);
  }
  await store.dispatch(COMMONS.GUEST_SEND_MFA_REQUEST, data);
};

const handleRouting = async (info) => {
  if (info === 'previous') {
    store.state.guestLogin.contactUpdateStep = 2;
    store.state.networkResponse = null;
  } else if (info === 'next') {
    // Check the store for a pending redirect instead of the URL
    const redirectTo = store.state.guestLogin.navigationSource;

    if (redirectTo) {
      // 1. Refresh personal info to get the new 'isEmailVerified' status
      try {
        await store.dispatch(COMMONS.GUEST_PROFILE); 
      } catch (error) {
        console.error("Failed to refresh user data", error);
      }
      // Reset the store value so it doesn't redirect again next time
      store.state.guestLogin.navigationSource = null;
      
      router.push({ name: redirectTo });
    } else {
      router.push({ name: 'Profile' });
    }
  }
};
</script>


<style lang="scss" scoped>
.user-con-validate {
  display: flex;
  gap: 2rem;
  margin: 2rem 0rem;
  .user-con-details {
    display: flex;
    gap: 4rem;
    width: 50%;
    .user-details{
      width: 50%;
      .verify-updateContact{
        display: flex;
        width: 335px;
        label {
          word-wrap: break-word;
          width: 100%;
        }
        .verify-icon{
          width: 50px;
          height: 20px;
          margin-left: 5px;
        }
      }
      input {
        width: 200px;
      }
      .user-phone-details{
        display: flex;
        align-items: baseline;
        width: 335px;
        .user-phone-text{
          margin-left: 5px;
          display: flex;
          font-weight: 300;
          @media only screen and (max-width: 900px) {
            word-wrap: nowrap;
          }
        }
        .user-phone-heading{
          font-weight: bold;
        }
      }
      .err-msg{
        color: var(--ri-red);
      }
      .user-email-heading{
        font-weight: bold;
      }
    }
    @media only screen and (max-width: 699px) {
      display: block;
      .user-details{
        width: 50px;
        .verify-updateContact{
          width: 300px;
        }
        .login-code-text{
          width: 100px;
        }
      }
    }

    @media only screen and (min-width: 700px) and (max-width: 1250px) {
      .user-details{
        .verify-updateContact {
          label {
            width: 200px;
          }
        }
        .user-phone-details {
          width: 200px;
        }
      }
    }
    @media only screen and (min-width: 900px) and (max-width: 1215px) {
      .user-details{
        input {
          width: 145px;
        }
      }
    }
  }
  .user-response {
    margin-top: 2rem;
    display: flex;
    .btn-otp{
      button {
        margin-right: 2rem;
      }
      .resend-code{
        background-color: white;
        color: var(--ri-blue);

        &:hover{
          background-color: var(--ri-lighter-blue)
        }
      }
    }
    @media only screen and (max-width: 899px) {
      .btn-otp{
        .app-btn-primary {
          margin-bottom: 1rem;
        }
      }
    }
    @media only screen and (min-width: 900px) and (max-width: 1160px) {
      display: flex;
      .btn-otp{
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        button {
          margin-right: 2rem;
          min-width: 207px;
        }
      }
    }
  }
  @media only screen and (max-width: 899px) {
    display: block;
  }
}
.err-email{
  color: var(--ri-red);
}
.err-phone{
  color: var(--ri-red);
}
.app-button-group {
  margin-top: 1rem;
  .app-btn-primary {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    margin-left: 1rem;
    &[disabled] {
    cursor: not-allowed;
    opacity: 0.7;
    }
  }
}
</style>  